import React from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"

interface VolumePitchMisalignmentConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const PRESET_OPTIONS = [
  {
    key: "strict",
    label: "Strict",
    description: "Detects smaller inconsistencies",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Checks noticeable emphasis changes",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags strong emphasis changes",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const VolumePitchMisalignmentConfig: React.FC<
  VolumePitchMisalignmentConfigProps
> = ({ metricRecordData, onChange }) => {
  const hasLegacyFields =
    !metricRecordData.metric_metadata?.threshold_preset &&
    metricRecordData.metric_metadata?.min_volume_change_for_pitch_alignment !==
      undefined
  const currentPreset = hasLegacyFields
    ? "custom"
    : (metricRecordData.metric_metadata?.threshold_preset ?? "normal")
  const isCustom = currentPreset === "custom"

  const handlePresetChange = (value: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        threshold_preset: value as "strict" | "normal" | "lenient" | "custom",
      },
    })
  }

  const handleMinVolumeChangeForPitchAlignmentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_volume_change_for_pitch_alignment: isNaN(value) ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Threshold Preset</span>
        </div>
        <Select value={currentPreset} onValueChange={handlePresetChange}>
          <SelectTrigger aria-label="Threshold Preset">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Select a preset or choose Custom to define your own thresholds.
        </p>
      </div>

      {isCustom && (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <span className={LABEL_STYLES}>Volume Change Threshold (dB)</span>
            <Tooltip content="Minimum volume change required for pitch misalignment calculation.">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            type="number"
            step="1"
            min="0"
            value={
              metricRecordData.metric_metadata
                ?.min_volume_change_for_pitch_alignment ?? ""
            }
            onChange={handleMinVolumeChangeForPitchAlignmentChange}
            placeholder="7"
            aria-label="Minimum Volume Change for Pitch Misalignment"
          />
        </div>
      )}
    </div>
  )
}
